import { api } from '../console/api.ts';
import { empty, field, pageHead, pill } from '../console/components.ts';
import { InputError } from '../console/errors.ts';
import { date, esc, icon } from '../console/format.ts';
import { type Actions, current, render } from '../console/router.ts';
import { administersNubi } from '../console/session.ts';
import { $, act, textInput } from '../console/ui.ts';
import { REQUIRED_HINT, textControl } from '../controls.ts';

/**
 * «Administración de Nubi › Aseguradoras» (#283, ADR-0027): the carriers Nubi integrates (today
 * Seguros G&T) with their status in the platform — Activa, En pruebas, Apagada —, their public
 * data (name, contact and the address of their environment, informative only) and the agencies
 * that pilot one in testing. The status decides only what «Cotizar» offers new: Apagada takes the
 * carrier out of «Cotizar» for every agency, En pruebas leaves it only to its pilot agencies
 * (labelled «En pruebas»). What was issued goes on: the screen says so before each change, which
 * is confirmed here (never a browser dialog). Its view id is `aseguradoras-nubi`: `aseguradoras`
 * is the agency's «Remesas y conciliación».
 */

export type CarrierStatus = 'ACTIVE' | 'TESTING' | 'OFF';
export type PlatformCarrier = {
  carrier: string;
  name: string;
  status: CarrierStatus;
  contact: { name: string | null; email: string | null; phone: string | null };
  environmentUrl: string | null;
  updatedAt: string | null;
  agencies: { id: string; name: string; enabled: boolean; pilot: boolean }[];
  pilots: string[];
};

/** Each status in the person's words, its pill and what it means for the agencies. */
export const CARRIER_STATUS: Record<
  CarrierStatus,
  { label: string; tone: string; action: string; means: string }
> = {
  ACTIVE: {
    label: 'Activa',
    tone: 'ok',
    action: 'Activar',
    means: 'Todas las agencias que trabajan con ella la cotizan y emiten pólizas nuevas.',
  },
  TESTING: {
    label: 'En pruebas',
    tone: 'warn',
    action: 'Poner en pruebas',
    means:
      'Solo sus agencias piloto la ven en Cotizar, con la etiqueta «En pruebas», y emiten con ella.',
  },
  OFF: {
    label: 'Apagada',
    tone: 'bad',
    action: 'Apagar',
    means: 'Ninguna agencia la cotiza ni emite pólizas nuevas con ella.',
  },
};
const ISSUED_GOES_ON =
  'Lo ya emitido no cambia: sus pólizas se siguen cobrando, renovando, endosando y conciliando.';
const SECONDS = 'El cambio llega a todas las agencias en unos segundos.';

/** The carrier whose detail is open, and the status change waiting for its confirmation. */
type Panel = { carrier: string; confirm?: CarrierStatus | undefined };
let panel: Panel | null = null;
/** The carriers of the last paint (the forms and confirmations read them). */
let shown: PlatformCarrier[] = [];

/** Back to the plain list (a new session, or the menu). */
export function resetAseguradorasNubi() {
  panel = null;
  shown = [];
}

const carrierOf = (id: string) => shown.find((c) => c.carrier === id);
const statusPill = (s: CarrierStatus) => pill(CARRIER_STATUS[s].tone, CARRIER_STATUS[s].label);
const contactText = (c: PlatformCarrier) =>
  [c.contact.name, c.contact.email, c.contact.phone].filter(Boolean).join(' · ') || '—';

/** The confirmation of a status change: what it means for the agencies, then a deliberate click. */
function confirmHtml(c: PlatformCarrier, to: CarrierStatus) {
  const s = CARRIER_STATUS[to];
  const pilots = c.agencies.filter((a) => a.pilot && a.enabled).map((a) => a.name);
  const pilotsText =
    to === 'TESTING'
      ? pilots.length
        ? ` Hoy sus agencias piloto son: ${pilots.join(', ')}.`
        : ' Todavía no tiene agencias piloto: márcalas abajo para que alguna la vea.'
      : '';
  const tone = to === 'OFF' ? 'bad' : to === 'TESTING' ? 'warn' : 'info';
  return `<section class="card" role="alertdialog" aria-modal="false" aria-labelledby="carrier-confirm-title" aria-describedby="carrier-confirm-text" data-component="carrier-confirm">
    <div class="card-head"><h2 id="carrier-confirm-title">¿${esc(s.action)} ${esc(c.name)}?</h2></div>
    <p class="note ${tone}" id="carrier-confirm-text">${icon(to === 'ACTIVE' ? 'ok' : 'alert')}<span>${esc(`${s.means}${pilotsText} ${ISSUED_GOES_ON} ${SECONDS}`)}</span></p>
    <div class="form-foot"><span class="spacer"></span>
      <button type="button" class="btn" data-carrier-cancel="${esc(c.carrier)}">Cancelar</button>
      <button type="button" class="btn ${to === 'OFF' ? 'danger' : 'primary'}" data-carrier-status-confirm="${esc(c.carrier)}" data-status="${to}">Sí, ${esc(s.action.toLowerCase())}</button></div>
  </section>`;
}

/** A carrier's detail: its status (and the buttons that change it), its data and its pilots. */
function detailHtml(c: PlatformCarrier) {
  const v = (x: string | null) => x ?? '';
  const others = (Object.keys(CARRIER_STATUS) as CarrierStatus[]).filter((s) => s !== c.status);
  const statusButtons = others
    .map(
      (s) =>
        `<button type="button" class="btn sm ${s === 'OFF' ? 'danger' : ''}" data-carrier-status="${esc(c.carrier)}" data-status="${s}">${esc(CARRIER_STATUS[s].action)}</button>`,
    )
    .join(' ');
  const agencies = c.agencies.length
    ? `<div class="table-wrap" data-component="table"><table class="table" data-component="carrier-pilots"><caption class="sr-only">Agencias que trabajan con ${esc(c.name)}</caption>
      <thead><tr><th scope="col">Agencia</th><th scope="col">Trabaja con ella</th><th scope="col">Piloto</th><th scope="col" class="actions">Acción</th></tr></thead><tbody>
      ${c.agencies
        .map(
          (
            a,
          ) => `<tr data-pilot-agency="${esc(a.id)}"><td><strong>${esc(a.name)}</strong> <span class="muted small">${esc(a.id)}</span></td>
          <td>${a.enabled ? 'Sí' : 'No'}</td><td>${a.pilot ? pill('info', 'Piloto') : '<span class="muted">—</span>'}</td>
          <td class="actions">${
            a.pilot
              ? `<button type="button" class="btn sm" data-pilot-unset="${esc(a.id)}" data-carrier="${esc(c.carrier)}" aria-label="Quitar a ${esc(a.name)} como piloto de ${esc(c.name)}">Quitar de piloto</button>`
              : `<button type="button" class="btn sm" data-pilot-set="${esc(a.id)}" data-carrier="${esc(c.carrier)}" aria-label="Marcar a ${esc(a.name)} como piloto de ${esc(c.name)}">Marcar como piloto</button>`
          }</td></tr>`,
        )
        .join('')}</tbody></table></div>`
    : `<p class="muted">Ninguna agencia trabaja todavía con ${esc(c.name)}.</p>`;
  return `<section class="card" data-component="carrier-detail" aria-labelledby="carrier-detail-title">
    <div class="card-head"><h2 id="carrier-detail-title" tabindex="-1">${esc(c.name)}</h2><span class="spacer"></span>${statusPill(c.status)}</div>
    <p class="note ${c.status === 'OFF' ? 'bad' : c.status === 'TESTING' ? 'warn' : 'info'}">${icon(c.status === 'ACTIVE' ? 'ok' : 'alert')}<span>${esc(`${CARRIER_STATUS[c.status].means} ${ISSUED_GOES_ON}`)}</span></p>
    <div class="form-foot"><span class="muted small">Cambiar el estado:</span><span class="spacer"></span>${statusButtons}</div>
    <form id="carrier-form" class="form-grid" data-carrier="${esc(c.carrier)}" novalidate>
      ${REQUIRED_HINT}
      <fieldset><legend>Datos públicos</legend>
        ${field('Nombre', textControl({ id: 'carrier-name', value: c.name, maxlength: 120 }), { required: true })}
        ${field('URL de su ambiente', textControl({ id: 'carrier-url', type: 'url', value: v(c.environmentUrl), maxlength: 300, placeholder: 'https://', hint: 'Informativa: Nubi sigue llamando a la dirección configurada de la integración.' }))}
      </fieldset>
      <fieldset><legend>Contacto</legend>
        ${field('Nombre del contacto', textControl({ id: 'carrier-contact-name', value: v(c.contact.name), maxlength: 120, autocomplete: 'name' }))}
        ${field('Correo del contacto', textControl({ id: 'carrier-contact-email', type: 'email', value: v(c.contact.email), maxlength: 254, autocomplete: 'email' }))}
        ${field('Teléfono del contacto', textControl({ id: 'carrier-contact-phone', type: 'tel', value: v(c.contact.phone), maxlength: 20, autocomplete: 'tel' }))}
      </fieldset>
      <div class="form-foot"><span class="muted small">${c.updatedAt ? `Cambiada el ${esc(date(c.updatedAt.slice(0, 10)))}.` : 'Sin cambios desde que Nubi la integró.'}</span><span class="spacer"></span>
        <button type="button" class="btn" data-carrier-close>Cerrar</button>
        <button type="submit" class="btn primary" id="carrier-submit">Guardar datos</button></div>
    </form>
    <div class="card-head"><h3>Agencias piloto</h3></div>
    <p class="muted small">Mientras ${esc(c.name)} esté en pruebas, solo sus agencias piloto la ven en Cotizar. Para que otra agencia sea piloto, primero configúrala en «Agencias» › «Aseguradoras».</p>
    ${agencies}
  </section>`;
}

export async function aseguradorasNubi() {
  const description =
    'Las aseguradoras que Nubi integra: su estado en la plataforma (activa, en pruebas o apagada), sus datos públicos y sus agencias piloto.';
  if (!administersNubi())
    return `${pageHead('Aseguradoras', description)}${empty('lock', 'Esta sección es de la administración de Nubi', 'Tu rol no administra las aseguradoras de la plataforma.', '', 2)}`;
  const head = pageHead('Aseguradoras', description);
  const carriers = await api<PlatformCarrier[]>('GET', '/v1/platform/carriers');
  shown = carriers;
  if (carriers.length === 0)
    return `${head}${empty('building', 'Nubi todavía no integra aseguradoras', 'Cuando Nubi integre una aseguradora aparecerá aquí, activa.', '', 2)}`;
  const open = panel ? carrierOf(panel.carrier) : undefined;
  const top = open
    ? `${panel?.confirm ? confirmHtml(open, panel.confirm) : ''}${detailHtml(open)}`
    : '';
  const rows = carriers
    .map((c) => {
      const working = c.agencies.filter((a) => a.enabled).length;
      return `<tr data-platform-carrier="${esc(c.carrier)}"><td><strong>${esc(c.name)}</strong> <span class="muted small">${esc(c.carrier)}</span></td>
        <td>${statusPill(c.status)}</td><td>${esc(contactText(c))}</td>
        <td class="num">${working}</td><td>${
          c.pilots.length
            ? esc(
                c.agencies
                  .filter((a) => a.pilot)
                  .map((a) => a.name)
                  .join(', '),
              )
            : '<span class="muted">—</span>'
        }</td>
        <td class="actions"><button type="button" class="btn sm" data-carrier-open="${esc(c.carrier)}" aria-label="Abrir ${esc(c.name)}">Abrir</button></td></tr>`;
    })
    .join('');
  return `${head}${top}<section class="card" data-component="platform-carriers"><div class="card-head"><h2>Aseguradoras de la plataforma</h2><span class="spacer"></span><span class="muted small">${esc(SECONDS)}</span></div>
    <div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Aseguradoras que Nubi integra y su estado</caption>
    <thead><tr><th scope="col">Aseguradora</th><th scope="col">Estado</th><th scope="col">Contacto</th><th scope="col" class="num">Agencias</th><th scope="col">Pilotos</th><th scope="col" class="actions">Acción</th></tr></thead>
    <tbody>${rows}</tbody></table></div></section>`;
}

/** Paints the screen again and puts the focus where the person continues. */
async function repaint(focus?: string) {
  await render(current, { quiet: true });
  if (focus) document.querySelector<HTMLElement>(focus)?.focus();
}
const openPanel = (p: Panel | null, focus?: string) => {
  panel = p;
  void repaint(focus);
};
const value = (id: string) => ($(`#${id}`) as HTMLInputElement | null)?.value?.trim() ?? '';
const STATUSES = new Set<string>(Object.keys(CARRIER_STATUS));
const statusOf = (s: string | undefined): CarrierStatus | null =>
  s && STATUSES.has(s) ? (s as CarrierStatus) : null;

/** The body of a change: the carrier as it is, with what changes. */
const bodyOf = (c: PlatformCarrier, over: Partial<PlatformCarrier> = {}) => {
  const x = { ...c, ...over };
  return { name: x.name, status: x.status, contact: x.contact, environmentUrl: x.environmentUrl };
};

/** A pilot mark or its removal, then the detail again. */
const pilot = (b: HTMLElement, carrier: string, agencyId: string, set: boolean) => {
  const c = carrierOf(carrier);
  const a = c?.agencies.find((x) => x.id === agencyId);
  if (!c || !a) return;
  void act(b, set ? 'Marcando como piloto…' : 'Quitando de piloto…', async () => {
    await api(
      set ? 'PUT' : 'DELETE',
      `/v1/platform/carriers/${encodeURIComponent(carrier)}/pilots/${encodeURIComponent(agencyId)}`,
    );
    await repaint(`[data-pilot-agency="${agencyId}"] button`);
    return set ? `${a.name} es piloto de ${c.name}.` : `${a.name} ya no es piloto de ${c.name}.`;
  });
};

export const actions: Actions = {
  click: {
    carrierOpen: (_b, d) => openPanel({ carrier: d.carrierOpen ?? '' }, '#carrier-detail-title'),
    carrierClose: () => openPanel(null, '[data-carrier-open]'),
    carrierCancel: (_b, d) =>
      openPanel({ carrier: d.carrierCancel ?? '' }, '#carrier-detail-title'),
    carrierStatus: (_b, d) => {
      const to = statusOf(d.status);
      if (to) openPanel({ carrier: d.carrierStatus ?? '', confirm: to }, '#carrier-confirm-title');
    },
    carrierStatusConfirm: (b, d) => {
      const c = carrierOf(d.carrierStatusConfirm ?? '');
      const to = statusOf(d.status);
      if (!c || !to) return;
      void act(b, 'Cambiando el estado…', async () => {
        await api(
          'PUT',
          `/v1/platform/carriers/${encodeURIComponent(c.carrier)}`,
          bodyOf(c, { status: to }),
        );
        panel = { carrier: c.carrier };
        await repaint('#carrier-detail-title');
        return `${c.name} quedó ${CARRIER_STATUS[to].label.toLowerCase()}.`;
      });
    },
    pilotSet: (b, d) => pilot(b, d.carrier ?? '', d.pilotSet ?? '', true),
    pilotUnset: (b, d) => pilot(b, d.carrier ?? '', d.pilotUnset ?? '', false),
  },
  submit: {
    'carrier-form': (ev) => {
      ev.preventDefault();
      const form = ev.target as HTMLFormElement;
      const c = carrierOf(form.dataset.carrier ?? '');
      if (!c) return;
      void act(form.querySelector<HTMLElement>('[type="submit"]'), 'Guardando…', async () => {
        const name = textInput('#carrier-name', 'el nombre');
        const email = value('carrier-contact-email').toLowerCase();
        if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email))
          throw new InputError('Escribe un correo válido.', 'carrier-contact-email');
        const url = value('carrier-url');
        if (url && !/^https?:\/\/[^\s<>"']+$/.test(url))
          throw new InputError('Escribe una dirección que empiece con https://', 'carrier-url');
        await api(
          'PUT',
          `/v1/platform/carriers/${encodeURIComponent(c.carrier)}`,
          bodyOf(c, {
            name,
            contact: {
              name: value('carrier-contact-name') || null,
              email: email || null,
              phone: value('carrier-contact-phone') || null,
            },
            environmentUrl: url || null,
          }),
        );
        await repaint('#carrier-detail-title');
        return `Datos de ${name} guardados.`;
      });
    },
  },
};
